import { Link, Page, Text, View } from '@react-pdf/renderer'
import { RautReportIndication } from 'features/indicationTriage'
import {
  displayMin,
  displayWallLoss,
} from 'pages/reporting/reports/raut/sections/subSections/indicationDetailsComponents/indicationDisplay/utils/valueDisplayHelpers'
import { PageFooter } from 'pages/reporting/shared/pdfGeneration/components/shared/PageFooter'
import { PageHeader } from 'pages/reporting/shared/pdfGeneration/components/shared/PageHeader'
import { PageSubheader } from 'pages/reporting/shared/pdfGeneration/components/shared/PageSubHeader'
import { PDFStyles } from 'pages/reporting/shared/pdfGeneration/styles/PDFStyles'
import { SavedIndicationGraph } from 'pages/reporting/shared/types'
import React, { useCallback, useRef } from 'react'
import {
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
} from 'types'

interface IndicationSummaryPageProps {
  indications: RautReportIndication[]
  savedIndicationGraphs: Record<string, SavedIndicationGraph>
  scanIndicationMode: boolean
  indicationToNominalMap: ReadonlyMap<
    string,
    GeckoMeasurement | GeckoMeasurementRange
  >
  measurementPrecision: number
  onRender: (info: { id: string; pageNumber: number }) => void
  jobNumber?: string
  revisionNumber?: number
  geometricUnit?: GeckoMeasurementUnit
  distanceUnit?: GeckoMeasurementUnit
  reviewerSignedOff?: boolean
  supervisorSignedOff?: boolean
}

export const IndicationSummaryPage: React.FC<IndicationSummaryPageProps> = ({
  indications,
  savedIndicationGraphs,
  scanIndicationMode,
  indicationToNominalMap,
  measurementPrecision,
  onRender,
  jobNumber,
  revisionNumber,
  geometricUnit = GeckoMeasurementUnit.Millimeters,
  distanceUnit = GeckoMeasurementUnit.Millimeters,
  reviewerSignedOff,
  supervisorSignedOff,
}) => {
  const hasRegistered = useRef(false)

  // Get unit display string
  const dUnitStr = distanceUnit.id
  const gUnitStr = geometricUnit.id

  const decimals = distanceUnit === GeckoMeasurementUnit.Millimeters ? 0 : 3

  // TODO: Make the title handling better.
  const indicationTitleHandler = useCallback(
    (item: string) => (scanIndicationMode ? item.split('_')[0] : item),
    [scanIndicationMode]
  )

  // Split indications into chunks for pagination
  const FIRST_PAGE_ROWS = 20
  const SUBSEQUENT_PAGE_ROWS = 23
  const chunkArray = useCallback((array: RautReportIndication[]) => {
    const chunks = []
    let currentIndex = 0

    if (array.length > 0) {
      // First chunk (shorter)
      chunks.push(array.slice(0, Math.min(FIRST_PAGE_ROWS, array.length)))
      currentIndex = FIRST_PAGE_ROWS

      // Subsequent chunks (normal size)
      while (currentIndex < array.length) {
        chunks.push(
          array.slice(currentIndex, currentIndex + SUBSEQUENT_PAGE_ROWS)
        )
        currentIndex += SUBSEQUENT_PAGE_ROWS
      }
    }

    return chunks
  }, [])

  const indicationChunks = chunkArray(indications)

  return (
    <Page size='A4' style={PDFStyles.page} id='indications'>
      {/* Hidden text for page tracking */}
      <Text
        render={({ pageNumber }) => {
          if (!hasRegistered.current) {
            onRender({ id: 'indications', pageNumber })
            hasRegistered.current = true
          }
          return ''
        }}
      />
      <PageHeader
        title={
          scanIndicationMode ? 'Scan Summary Table' : 'Indication Summary Table'
        }
      />
      <View style={PDFStyles.contentWithPadding}>
        {/* Use the consistent PageSubheader component */}
        <PageSubheader title='Overview' align='left' color='black' />

        <Text style={PDFStyles.text}>
          {scanIndicationMode
            ? 'This table summarizes all of the scans collected during the inspection. Click on a scan name to navigate to its detailed view'
            : 'This table summarizes all indications found during the inspection. Click on an indication name to navigate to its detailed view in the Indication Details section.'}
        </Text>

        {/* Table Chunks */}
        {indicationChunks.map((chunk, chunkIndex) => (
          <View
            key={chunkIndex}
            break={chunkIndex > 0}
            style={{
              width: '100%',
              marginTop: chunkIndex === 0 ? 16 : 0,
              marginBottom: 30,
              fontSize: 8,
            }}>
            {/* Table Header for each chunk */}
            <View style={PDFStyles.tableHeader}>
              <View style={PDFStyles.tableHeaderCell15}>
                <Text>{scanIndicationMode ? 'Scan' : 'Indication'}</Text>
              </View>
              <View style={PDFStyles.tableHeaderCell20}>
                <Text>X Range ({dUnitStr})</Text>
              </View>
              <View style={PDFStyles.tableHeaderCell20}>
                <Text>Y Range ({dUnitStr})</Text>
              </View>
              <View style={PDFStyles.tableHeaderCell20}>
                <Text>Minimum ({gUnitStr})</Text>
              </View>
              <View style={PDFStyles.tableHeaderCell20}>
                <Text>% Wall Loss</Text>
              </View>
              <View style={PDFStyles.tableHeaderCell20}>
                <Text>DRA Rating</Text>
              </View>
            </View>

            {/* Table Rows for this chunk */}
            {chunk.map((indication, index) => {
              const globalIndex =
                chunkIndex === 0
                  ? FIRST_PAGE_ROWS * chunkIndex
                  : SUBSEQUENT_PAGE_ROWS * chunkIndex + index

              const nominalForThisIndication = indicationToNominalMap.get(
                indication.id
              )

              return (
                <View
                  key={indication.id}
                  style={
                    globalIndex % 2 === 0
                      ? PDFStyles.tableRowEven
                      : PDFStyles.tableRowOdd
                  }>
                  <View style={PDFStyles.tableCell15}>
                    {/* Create a link to the indication details section */}
                    <Link src={`#${indication.id}`} style={PDFStyles.link}>
                      <Text>{indicationTitleHandler(indication.title)}</Text>
                    </Link>
                  </View>
                  <View style={PDFStyles.tableCell20}>
                    <Text>
                      {indication.selection
                        ? `${indication.selection.x.min.toNumber(distanceUnit).toFixed(decimals)} - ${indication.selection.x.max.toNumber(distanceUnit).toFixed(decimals)}`
                        : 'N/A'}
                    </Text>
                  </View>
                  <View style={PDFStyles.tableCell20}>
                    <Text>
                      {indication.selection
                        ? `${indication.selection.y.min.toNumber(distanceUnit).toFixed(decimals)} - ${indication.selection.y.max.toNumber(distanceUnit).toFixed(decimals)}`
                        : 'N/A'}
                    </Text>
                  </View>
                  <View style={PDFStyles.tableCell20}>
                    <Text>
                      {indication.customInfo.stats?.minimum &&
                        displayMin(
                          indication.customInfo.stats.minimum,
                          geometricUnit,
                          measurementPrecision,
                          false
                        )}
                    </Text>
                  </View>
                  <View style={PDFStyles.tableCell20}>
                    <Text>
                      {indication.customInfo.stats?.minimum &&
                        displayWallLoss(
                          indication.customInfo.stats.minimum,
                          nominalForThisIndication ?? GeckoMeasurement.Inf(),
                          geometricUnit,
                          measurementPrecision,
                          false
                        )}{' '}
                    </Text>
                  </View>
                  <View style={PDFStyles.tableCell20}>
                    <Text>
                      {`${savedIndicationGraphs[indication.id]?.dra_rating ?? indication.customInfo.dra_rating} - ${savedIndicationGraphs[indication.id]?.sub_rating ?? indication.customInfo.sub_rating}`}
                    </Text>
                  </View>
                </View>
              )
            })}
          </View>
        ))}
        {/* Summary */}
        <View style={PDFStyles.summaryContainer}>
          <Text style={PDFStyles.text}>
            Total {scanIndicationMode ? 'Scans' : 'Indications'}{' '}
            {indications.length}
          </Text>
        </View>
      </View>
      <PageFooter
        jobNumber={jobNumber}
        revisionNumber={revisionNumber}
        prelim={!(reviewerSignedOff && supervisorSignedOff)}
      />{' '}
    </Page>
  )
}
